import type { DeclarationDApp, FicheInstanciee, InstanceDApp, ModeleDApp } from '@taki/ui';
import { alignerSurLesJeux, jeuxDeLOrg, lireEcransDeclares, migrerEcransDeclares } from '@taki/data-model';
import type { DefinitionDeJeu } from '@taki/data-model';
import type { AppLue, DeclarationLue, EcransLus, FicheLue } from '../modele.js';
import type { ShellAppEntry, ShellAppFiche } from '../../types.js';

/**
 * LA DECLARATION D'UNE ORG, VUE PAR LE MOTEUR DU STUDIO.
 *
 * CE QUE CE MODULE FAIT, ET RIEN D'AUTRE : il traduit, dans les deux sens,
 * entre ce que le SHELL a sous la main (une entree de registre, une instance
 * lue par le port) et ce que le MOTEUR du studio consomme (`AppLue`, la forme
 * d'une app lue). Tout y est PUR : aucun etat React, aucune lecture, aucune
 * ecriture. Ce qui se calcule ici se teste par `node --test`
 * (`packages/shell/tests/studio-client.test.mjs`).
 *
 * POURQUOI `AppLue` PLUTOT QU'UNE FORME A NOUS. Le moteur d'edition du Studio
 * (projections, `champsEdition`, `ecransEdition`) est ecrit contre cette
 * forme-la, et c'est TOUT l'interet du studio client : le client edite sa
 * declaration avec les memes lignes, les memes zones et les memes ecritures que
 * le Studio du depot. Une seconde forme aurait demande un second moteur.
 *
 * Les écrans d'instance sont relus et leurs anciens noms remplacés selon les
 * cibles du modèle. Le cliché reste intact pour la fusion ; ce que le Studio
 * écrit repasse par `port.valider` avant d'atteindre la base.
 */

/** La declaration du modele d'une app declaree, telle que son entree de registre la porte. */
export interface DeclarationModele {
  champs: unknown;
  ecrans: unknown;
  fiche: FicheInstanciee;
  /** La version de `taki.app.json` : elle date le cliche du modele. */
  version: string;
}

/**
 * LES QUATRE MOTS QU'UNE ORG PEUT CHANGER, lus sur la fiche du modele.
 *
 * LA LISTE EST FERMEE, et elle est la MEME a quatre endroits qui doivent dire
 * la meme chose : les Security Rules (`fiche.keys().hasOnly`), le schema du SDK
 * (`ficheInstancieeSchema`), la projection `ficheDeclaree` de
 * `@taki/data-model`, et ce panneau, qui en rend exactement quatre lignes. Ce
 * qui reste dehors est ce qui IDENTIFIE l'app (`id`, `version`, `entities`,
 * `menu.route`) : un client renomme ce qu'il lit, jamais ce par quoi le socle
 * la trouve.
 *
 * `name` NE PEUT PAS MANQUER : une app sans nom ne s'affiche nulle part. Le
 * libelle de menu tient lieu de repli, c'est deja ce que le shell affiche.
 */
export function ficheDuModele(fiche: ShellAppFiche, repli: string): FicheInstanciee {
  return {
    name: fiche.name ?? repli,
    ...(fiche.description === undefined ? {} : { description: fiche.description }),
    icon: fiche.icon,
    menu: { label: fiche.menu.label },
  };
}

/** La declaration du modele d'une entree de registre, ou `null` : l'app n'est pas declaree. */
export function modeleDe(entry: ShellAppEntry): DeclarationModele | null {
  if (entry.declaration === undefined || entry.fiche === undefined) return null;
  return {
    champs: entry.declaration.champs,
    ecrans: entry.declaration.ecrans,
    fiche: ficheDuModele(entry.fiche, entry.label),
    /* SANS VERSION, PAS DE MISE A JOUR POSSIBLE, et c'est dit plutot que
       devine : une fiche sans `version` (un placeholder, un test) pose un
       cliche date `0`, que toute version reelle depasse. */
    version: entry.fiche.version ?? '0',
  };
}

/** La declaration du modele sous la forme commune (celle que `valider` et `fusionner` prennent). */
export function declarationDuModele(modele: DeclarationModele): DeclarationDApp {
  return {
    champs: modele.champs as DeclarationDApp['champs'],
    ecrans: modele.ecrans as DeclarationDApp['ecrans'],
    fiche: modele.fiche,
  };
}

/**
 * LE CLICHE DU MODELE : la declaration du modele, plus la version qui la portait.
 *
 * C'EST LA TROISIEME VOIE DE LA FUSION, et il n'existe nulle part ailleurs :
 * le bundle ne garde que sa version COURANTE. Il voyage donc avec l'instance,
 * et il ne bouge qu'a deux moments : la premiere copie, et l'application d'une
 * mise a jour.
 */
export function clicheDuModele(modele: DeclarationModele): ModeleDApp {
  return { ...declarationDuModele(modele), version: modele.version };
}

/**
 * LES JEUX QUE L'ORG A PERSONNALISES, lus dans toutes ses instances : un jeu
 * montre par deux apps a une seule definition (`docs/socle/jeu-listes.md`).
 */
export function jeuxDeLOrgDu(instances: ReadonlyMap<string, InstanceDApp> | null | undefined): ReadonlyMap<string, DefinitionDeJeu> {
  return jeuxDeLOrg(instances ?? new Map());
}

/**
 * LA DECLARATION QUE LE PANNEAU EDITE : celle de l'org quand elle existe, celle
 * du bundle sinon. La premiere personnalisation part donc d'une COPIE ENTIERE
 * du modele, ce qui est exactement ce que le document porte.
 *
 * RECALEE SUR LES JEUX DE L'ORG (`jeux`, `jeuxDeLOrgDu`) : Parametres > Projets >
 * Taches montre et ecrit la liste « Service » reglee dans Timesheet, et ce que
 * le Studio enregistre porte la definition de l'org, jamais une copie ancienne.
 */
export function declarationCourante(
  modele: DeclarationModele,
  instance: InstanceDApp | null,
  jeux?: ReadonlyMap<string, DefinitionDeJeu>,
): DeclarationDApp {
  if (instance === null) return alignerSurLesJeux(declarationDuModele(modele), jeux);
  return alignerSurLesJeux({
    champs: instance.champs,
    ecrans: migrerEcransDeclares(lireEcransDeclares(instance.ecrans, 'instance Studio'), lireEcransDeclares(modele.ecrans, 'modèle Studio')),
    fiche: instance.fiche ?? modele.fiche,
  }, jeux);
}

/**
 * UNE APP LUE, FABRIQUEE DEPUIS UNE DECLARATION.
 *
 * LES CHAMPS QUE LE PONT DEV REMPLIT SONT VIDES ICI, et c'est exact : `portes`,
 * `vues`, `champs` et `cartes` sont ce que le pont lit DANS LE CODE d'une app
 * non migree. Une app declaree n'a pas ce code, et la projection les ignore des
 * qu'un fichier d'ecrans existe (`proprietesDe`). Les laisser vides dit la
 * verite ; les inventer ferait deux listes du meme fait.
 *
 * `source` EST CE QUE CHAQUE LIGNE AFFICHE dans sa colonne « d'ou ca vient » :
 * ici, le document de l'org ou la declaration du bundle, jamais un chemin de
 * fichier du depot, qu'un client n'a pas.
 */
export function appLueDe(
  appId: string,
  fiche: FicheInstanciee,
  declaration: DeclarationDApp,
  source: string,
): AppLue {
  const lu = <T,>(valeur: T): { valeur: T; source: string; empreinte: null } => ({
    valeur,
    source,
    empreinte: null,
  });
  return {
    id: appId,
    identite: lu({ ...fiche, id: appId } as FicheLue),
    registre: lu(null),
    portes: lu([]),
    vues: lu([]),
    champs: lu([]),
    declares: lu(declaration.champs as unknown as DeclarationLue),
    ecrans: lu(declaration.ecrans as unknown as EcransLus),
    cartes: lu([]),
    nonLisible: [],
  };
}

/** Les portes de la declaration, dans l'ordre du fichier d'ecrans. */
export function portesDeLaDeclaration(
  declaration: DeclarationDApp,
): { id: string; label: string }[] {
  const ecrans = declaration.ecrans as unknown as EcransLus;
  return (ecrans.ecrans ?? []).flatMap((ecran) =>
    (ecran.portes ?? []).map((porte) => ({ id: porte.id, label: porte.label })),
  );
}

/** Une porte, avec le RANG de l'ecran qui la porte et le jeu qu'elle montre. */
export interface PorteEditable {
  id: string;
  label: string;
  /** Le rang de son ecran : c'est lui que tout id d'edition vise (`ecran:<r>:...`). */
  ecran: number;
  /** La clef du jeu montre (`famille::type`), ou `null` : la porte n'en pointe aucun. */
  jeu: string | null;
  /** Combien de stats elle declare : le chassis n'en rend que trois. */
  stats: number;
}

/**
 * LES PORTES, AVEC CE QU'IL FAUT POUR LES EDITER.
 *
 * ELLE NE REMPLACE PAS `portesDeLaDeclaration`, qui repond a une autre question
 * (« comment s'appelle la porte de ce chemin de fusion ») et dont la forme est
 * lue par la fenetre de mise a jour. Celle-ci repond a « ou ecrire », et le
 * RANG DE L'ECRAN en est le coeur : un id d'edition le porte, et le deviner a
 * zero marcherait tant qu'une app n'a qu'un ecran, puis ecrirait dans le
 * mauvais, en silence.
 */
export function portesEditables(declaration: DeclarationDApp): PorteEditable[] {
  const ecrans = declaration.ecrans as unknown as EcransLus;
  return (ecrans.ecrans ?? []).flatMap((ecran, rang) =>
    (ecran.portes ?? []).map((porte) => ({
      id: porte.id,
      label: porte.label,
      ecran: rang,
      jeu: porte.jeu === undefined ? null : `${porte.jeu.family}::${porte.jeu.type ?? ''}`,
      stats: (porte.stats ?? []).length,
    })),
  );
}

/**
 * LES AUTRES APPS DECLAREES ET LEURS PORTES, telles que l'org les a : de quoi
 * offrir « Ouvrir dans <app> › <porte> » au clic sur une fiche (chantier
 * `fiche-suivi`). Une app codee (sans declaration) n'a pas de porte a viser ;
 * une porte hors navigation reste offerte, on y entre justement par ce geste.
 *
 * CHAQUE PORTE DIT SON JEU (`jeu`, famille et type) : une fiche ne s'ouvre que
 * dans une porte de son jeu (`memeJeuDePorte`, `@taki/data-model`), et c'est
 * le Studio qui filtre l'offre avec, le schema d'une app ne voyant pas les
 * autres. Une porte sans jeu n'est jamais offerte : elle n'a pas de fiche.
 */
export function autresAppsDeclarees(
  entries: readonly ShellAppEntry[],
  appId: string,
  instances?: ReadonlyMap<string, InstanceDApp>,
): AutreAppDeclaree[] {
  const jeux = jeuxDeLOrgDu(instances);
  return entries.flatMap((entry) => {
    if (entry.id === appId) return [];
    const modele = modeleDe(entry);
    if (modele === null) return [];
    const declaration = declarationCourante(modele, instances?.get(entry.id) ?? null, jeux);
    const portes = declaration.ecrans.ecrans.flatMap((ecran) => ecran.portes.flatMap((porte) => porte.jeu === undefined
      ? []
      : [{ id: porte.id, label: porte.label, jeu: { family: porte.jeu.family, ...(porte.jeu.type === undefined ? {} : { type: porte.jeu.type }) } }]));
    return portes.length === 0 ? [] : [{ id: entry.id, nom: entry.label, portes }];
  });
}

/** Une autre app declaree de l'org, telle que « Ouvrir dans » l'offre. */
export interface AutreAppDeclaree {
  id: string;
  nom: string;
  portes: { id: string; label: string; jeu: { family: string; type?: string } }[];
}
